/**
 * User Store - Pinia store for managing user authentication and profile
 * Handles user login, profile management, and permissions
 */

import { defineStore } from "pinia";
import { ref, computed } from "vue";
import {
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  setPersistence,
  browserLocalPersistence,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions, COLLECTIONS, CURRENT_POLICY_VERSION } from "../services/firebase";
import { getRolePermissions } from "../config/permissions";

export const useUserStore = defineStore("user", () => {
  // State
  const user = ref(null);
  const userProfile = ref(null);
  const customClaims = ref(null);
  const loading = ref(false);
  const error = ref(null);
  const isAuthenticated = ref(false);
  const authStateReady = ref(false);
  
  // Flag เพื่อป้องกันการทำงานซ้ำซ้อนระหว่าง initializeAuth และ postLogin
  const isInitializing = ref(false);

  // Getters
  const displayName = computed(() => {
    return userProfile.value?.displayName || user.value?.displayName || "User";
  });

  const email = computed(() => {
    return user.value?.email || "";
  });

  const hasOrganization = computed(() => {
    return customClaims.value?.orgId !== null && customClaims.value?.orgId !== undefined;
  });

  const currentOrgId = computed(() => {
    return customClaims.value?.orgId || null;
  });

  const role = computed(() => {
    return customClaims.value?.role || null;
  });

  const tier = computed(() => {
    return customClaims.value?.tier || 'free';
  });

  const policyVersion = computed(() => {
    return customClaims.value?.policyVersion || null;
  });

  const preferredLanguage = computed(() => {
    return userProfile.value?.preferredLanguage || "th";
  });

  const permissions = computed(() => {
    return getRolePermissions(role.value);
  });

  const canAccess = computed(() => {
    return (permission) => permissions.value.includes(permission);
  });

  const hasRole = computed(() => {
    return (roles) => {
      if (Array.isArray(roles)) {
        return roles.includes(role.value);
      }
      return role.value === roles;
    };
  });

  // Actions
  const initializeAuth = () => {
    onAuthStateChanged(auth, async (firebaseUser) => {
      // Skip if already initializing (prevent duplicate calls from postLogin)
      if (isInitializing.value) {
        authStateReady.value = true;
        return;
      }
      
      if (firebaseUser) {
        user.value = firebaseUser;
        isAuthenticated.value = true;
        
        // Load profile and claims only if not already loaded
        // This prevents duplicate calls when postLogin already handled it
        if (!userProfile.value || userProfile.value.id !== firebaseUser.uid) {
          await loadUserProfile(firebaseUser.uid);
        }
        
        // Only refresh claims if not already set
        if (!customClaims.value || customClaims.value.orgId === undefined) {
          await refreshClaims();
        }
      } else {
        // Clear state on logout
        user.value = null;
        userProfile.value = null;
        customClaims.value = null;
        isAuthenticated.value = false;
      }
      authStateReady.value = true;
    });
  };

  const waitForAuthState = async () => {
    if (authStateReady.value) return;
    
    return new Promise((resolve) => {
      const unwatch = onAuthStateChanged(auth, () => {
        unwatch();
        resolve();
      });
    });
  };

  const loadUserProfile = async (userId) => {
    // Validation: ตรวจสอบ userId
    if (!userId || typeof userId !== 'string' || userId.trim() === '') {
      console.error("Invalid user ID provided to loadUserProfile");
      return;
    }

    try {
      const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, userId));
      if (userDoc.exists()) {
        userProfile.value = {
          id: userDoc.id,
          ...userDoc.data(),
        };
      } else {
        // User profile doesn't exist yet, it will be created by onUserCreate Cloud Function
        console.log("User profile not found, waiting for Cloud Function to create it...");
        userProfile.value = null;
      }
    } catch (err) {
      console.error("Error loading user profile:", err);
      // Don't throw error, just log it
      userProfile.value = null;
    }
  };

  const refreshClaims = async () => {
    try {
      if (!user.value) {
        console.warn("Cannot refresh claims: no authenticated user");
        return;
      }

      // Force token refresh
      await user.value.getIdToken(true);
      const tokenResult = await user.value.getIdTokenResult();
      
      customClaims.value = {
        orgId: tokenResult.claims.orgId || null,
        role: tokenResult.claims.role || null,
        tier: tokenResult.claims.tier || null,
        policyVersion: tokenResult.claims.policyVersion || null,
      };
    } catch (err) {
      console.error("Error refreshing claims:", err);
      // Don't throw, just log - claims might not be set yet
    }
  };

  const signIn = async () => {
    loading.value = true;
    error.value = null;

    try {
      // ทำให้ session คงอยู่
      await setPersistence(auth, browserLocalPersistence);

      const provider = new GoogleAuthProvider();
      provider.addScope("email");
      provider.addScope("profile");
      provider.setCustomParameters({ prompt: "select_account" });

      // 1) พยายาม popup ก่อน
      try {
        const result = await signInWithPopup(auth, provider);
        await postLogin(result.user.uid);
        return result.user;
      } catch (e) {
        // 2) ถ้า popup ถูกบล็อก/ปิด → fallback เป็น redirect
        if (e?.code === "auth/popup-blocked" || e?.code === "auth/popup-closed-by-user") {
          await signInWithRedirect(auth, provider);
          // หลัง redirect กลับมา ให้หน้า init เรียก handleRedirectResult()
          return;
        }
        throw e;
      }
    } catch (err) {
      console.error("Error signing in:", err);
      switch (err.code) {
        case "auth/admin-restricted-operation":
          error.value = "Google Sign-In ถูกปิดหรือโดเมนไม่ได้รับอนุญาต ตรวจการตั้งค่าใน Firebase Console.";
          break;
        case "auth/network-request-failed":
          error.value = "เครือข่ายมีปัญหา กรุณาลองใหม่อีกครั้ง.";
          break;
        case "auth/popup-closed-by-user":
          error.value = "Sign-in was cancelled. Please try again.";
          break;
        case "auth/popup-blocked":
          error.value = "Popup was blocked by your browser. Please allow popups and try again.";
          break;
        default:
          error.value = err.message || "เกิดข้อผิดพลาดระหว่างเข้าสู่ระบบ";
      }
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const switchOrganization = async (orgId) => {
    // Validation: ตรวจสอบ orgId
    if (!orgId || typeof orgId !== 'string' || orgId.trim() === '') {
      const errorMsg = 'Invalid organization ID';
      error.value = errorMsg;
      throw new Error(errorMsg);
    }

    try {
      loading.value = true;
      error.value = null;

      const switchOrgFn = httpsCallable(functions, "switchOrgV2");
      await switchOrgFn({ orgId });

      await refreshClaims();
      
      return true;
    } catch (err) {
      error.value = err.message;
      console.error("Error switching organization:", err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const signOutUser = async () => {
    try {
      loading.value = true;
      error.value = null;

      await signOut(auth);
      user.value = null;
      userProfile.value = null;
      customClaims.value = null;
      isAuthenticated.value = false;
    } catch (err) {
      error.value = err.message;
      console.error("Error signing out:", err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const clearError = () => {
    error.value = null;
  };

  // หลังล็อกอินสำเร็จ: สร้าง/อัปเดตโปรไฟล์ + refresh claims
  const postLogin = async (uid) => {
    // Validation: ตรวจสอบ uid
    if (!uid || typeof uid !== 'string' || uid.trim() === '') {
      throw new Error('Invalid user ID');
    }

    // Set flag to prevent duplicate initialization in initializeAuth
    isInitializing.value = true;
    
    try {
      await ensureUserDoc(uid);     // สร้างเอกสาร users/{uid} ถ้ายังไม่มี
      await loadUserProfile(uid);   // โหลด profile หลังจาก ensure
      await refreshClaims();        // ดึง custom claims ล่าสุด
      
      isAuthenticated.value = true;
      user.value = auth.currentUser;
      
      // Check for pending invite after login
      const pendingInviteId = localStorage.getItem('pendingInviteId');
      if (pendingInviteId) {
        localStorage.removeItem('pendingInviteId');
        // Redirect to accept-invite with the stored invite ID
        window.location.href = `/accept-invite?t=${pendingInviteId}`;
      }
    } finally {
      isInitializing.value = false;
    }
  };

  // สร้าง user doc ฝั่ง client เป็น fallback (กรณี onUserCreate ยังไม่พร้อม)
  const ensureUserDoc = async (uid) => {
    // Validation: ตรวจสอบ uid และ auth state
    if (!uid || typeof uid !== 'string' || uid.trim() === '') {
      throw new Error('Invalid user ID');
    }

    if (!auth.currentUser) {
      throw new Error('No authenticated user');
    }

    try {
      const ref = doc(db, COLLECTIONS.USERS, uid);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        await setDoc(ref, {
          email: auth.currentUser?.email ?? null,
          displayName: auth.currentUser?.displayName ?? null,
          photoURL: auth.currentUser?.photoURL ?? null,
          organizations: [],
          currentOrganization: null,
          preferredLanguage: 'th',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        }, { merge: true });
      }
    } catch (err) {
      console.error("Error ensuring user doc:", err);
      throw err; // Re-throw to let caller handle
    }
  };

  // เรียกใน main/router init หลังกลับจาก redirect
  const handleRedirectResult = async () => {
    try {
      const result = await getRedirectResult(auth);
      if (result?.user) {
        await postLogin(result.user.uid);
        return result.user;
      }
    } catch (e) {
      console.error("Redirect result error:", e);
    }
  };

  const updateLanguagePreference = async (language) => {
    // Validation: ตรวจสอบ authentication
    if (!user.value) {
      throw new Error("User not authenticated");
    }

    // Validation: ตรวจสอบ language code
    const validLanguages = ['th', 'en', 'id', 'vi', 'ms', 'zh', 'ja', 'ko'];
    if (!language || !validLanguages.includes(language)) {
      const errorMsg = `Invalid language code. Must be one of: ${validLanguages.join(', ')}`;
      error.value = errorMsg;
      throw new Error(errorMsg);
    }

    try {
      loading.value = true;
      error.value = null;

      const userDocRef = doc(db, COLLECTIONS.USERS, user.value.uid);
      await updateDoc(userDocRef, {
        preferredLanguage: language,
        updatedAt: serverTimestamp(),
      });

      // Update local user profile
      if (userProfile.value) {
        userProfile.value.preferredLanguage = language;
      }
    } catch (err) {
      error.value = err.message;
      console.error("Error updating language preference:", err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  return {
    // State
    user,
    userProfile,
    customClaims,
    loading,
    error,
    isAuthenticated,
    authStateReady,

    // Getters
    displayName,
    email,
    hasOrganization,
    currentOrgId,
    role,
    tier,
    policyVersion,
    preferredLanguage,
    permissions,
    canAccess,
    hasRole,

    // Actions
    initializeAuth,
    waitForAuthState,
    refreshClaims,
    signIn,
    switchOrganization,
    signOutUser,
    updateLanguagePreference,
    clearError,
    postLogin,
    ensureUserDoc,
    loadUserProfile,
    handleRedirectResult,
  };
});
